import { portalLinkBase } from '@/lib/current-firm';
import { supabase } from '@/lib/supabase';
import type {
  DesignerContactRow, ContractorAssignmentRow, ContractorPortalTokenRow, PortalViewScope,
} from '@/types/database';

export async function listSubcontractors(projectId: string): Promise<DesignerContactRow[]> {
  // Contacts are designer-owned since 0018, so "on this project" is a
  // membership question — hence the join through project_contacts rather than
  // a project_id column that no longer exists.
  const { data, error } = await supabase
    .from('project_contacts')
    .select('designer_contacts!inner(*)')
    .eq('project_id', projectId)
    .eq('designer_contacts.is_subcontractor', true);
  if (error) throw error;
  return (data ?? [])
    .map((r) => (r as unknown as { designer_contacts: DesignerContactRow }).designer_contacts)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function listAssignmentsForContact(
  contactId: string,
): Promise<ContractorAssignmentRow[]> {
  const { data, error } = await supabase
    .from('contractor_assignments')
    .select('*')
    .eq('contact_id', contactId);
  if (error) throw error;
  return data ?? [];
}

export async function listAssignmentsForProject(
  projectId: string,
): Promise<ContractorAssignmentRow[]> {
  // The project used to be reachable through the contact. Now the assignment's
  // schedule entry is what's project-scoped, so scoping goes through
  // schedule_entries -> rooms instead.
  const { data, error } = await supabase
    .from('contractor_assignments')
    .select('*, schedule_entries!inner(rooms!inner(project_id))')
    .eq('schedule_entries.rooms.project_id', projectId);
  if (error) throw error;
  return (data ?? []).map((r) => {
    const { schedule_entries: _se, ...rest } =
      r as ContractorAssignmentRow & { schedule_entries: unknown };
    return rest as ContractorAssignmentRow;
  });
}

export async function assignContractor(
  contactId: string,
  scheduleEntryId: string,
): Promise<ContractorAssignmentRow> {
  const { data, error } = await supabase
    .from('contractor_assignments')
    .insert({ contact_id: contactId, schedule_entry_id: scheduleEntryId })
    .select()
    .single();
  if (error) throw error;
  return data as ContractorAssignmentRow;
}

export async function unassignContractor(assignmentId: string): Promise<void> {
  const { error } = await supabase
    .from('contractor_assignments')
    .delete()
    .eq('id', assignmentId);
  if (error) throw error;
}

export async function getActivePortalToken(
  contactId: string,
  projectId: string,
): Promise<ContractorPortalTokenRow | null> {
  // (contact, project), never contact alone: one person can hold a portal link
  // for several of the designer's projects, and each must stay separate.
  const { data, error } = await supabase
    .from('contractor_portal_tokens')
    .select('*')
    .eq('contact_id', contactId)
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

/**
 * Every portal token on a project, keyed by contact.
 *
 * Bulk-loaded so the contact list can render a "Send portal link" button with
 * the URL already in the DOM. Resolving a token at click time would spend the
 * user gesture and stop navigator.share() from opening on iOS.
 */
export async function listPortalTokensForProject(
  projectId: string,
): Promise<Map<string, ContractorPortalTokenRow>> {
  const { data, error } = await supabase
    .from('contractor_portal_tokens')
    .select('*')
    .eq('project_id', projectId);
  if (error) throw error;
  const out = new Map<string, ContractorPortalTokenRow>();
  for (const row of (data ?? []) as ContractorPortalTokenRow[]) {
    out.set(row.contact_id, row);
  }
  return out;
}

export async function rotateContractorToken(
  contactId: string,
  projectId: string,
  expiresAt: string | null = null,
  viewScope: PortalViewScope = 'assigned',
): Promise<string> {
  // Use the same generator the share function defines.
  const { data: tokenData, error: genErr } = await supabase.rpc('gen_share_token');
  if (genErr) throw genErr;
  const token = String(tokenData);

  // Clear this contact's token FOR THIS PROJECT only. Without the project
  // filter, re-issuing one contractor's link would silently revoke their
  // access to every other project they're on.
  const { error: delErr } = await supabase
    .from('contractor_portal_tokens')
    .delete()
    .eq('contact_id', contactId)
    .eq('project_id', projectId);
  if (delErr) throw delErr;

  const { error: insErr } = await supabase
    .from('contractor_portal_tokens')
    .insert({
      contact_id: contactId, project_id: projectId, token,
      expires_at: expiresAt, view_scope: viewScope,
    });
  if (insErr) throw insErr;
  return token;
}

export async function clearContractorToken(
  contactId: string,
  projectId: string,
): Promise<void> {
  // Scoped for the same reason as rotate: revoking one project's link must not
  // revoke the others.
  const { error } = await supabase
    .from('contractor_portal_tokens')
    .delete()
    .eq('contact_id', contactId)
    .eq('project_id', projectId);
  if (error) throw error;
}

export function buildContractorUrl(token: string): string {
  return `${portalLinkBase()}#/contractor/${token}`;
}
